import { ApplicationType } from '@application/types';
import { useRouter } from '@application/utils';
import type { AccountGetBreederApplicationsQuery } from '@gql/graphql';
import {
  COLOR_PER_DOG_BREEDER_APPLICATION_STATUS,
  PLACEHOLDER_IMAGE_USER,
} from '@presentation/constants';
import type { MtApplicationsPageData } from '@presentation/pages';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';

type Application = NonNullable<
  NonNullable<AccountGetBreederApplicationsQuery['accountMe']>['dogBreeder']
>['applications'][number];

export interface UseFormatApplicationsParams {
  applications: Application[];
  onActionsClick?: (
    application: Application,
  ) => MtApplicationsPageData['cards'][0]['onActionsClick'];
  type?: ApplicationType;
}

export const useFormatApplications = (params: UseFormatApplicationsParams) => {
  const { routeLocationDescriptor, translate } = useRouter();
  const { onActionsClick, applications, type } = params;

  const formatApplicationsCard = useCallback(
    (application: Application): MtApplicationsPageData['cards'][0] => {
      let link = routeLocationDescriptor('mtApplications', { params: [application.id.toString()] });
      if (type === ApplicationType.OWNERS) {
        link = routeLocationDescriptor('mtOwners', { params: [application.id.toString()] });
      } else if (type === ApplicationType.WAITING_LIST) {
        link = routeLocationDescriptor('mtWaitingList', { params: [application.id.toString()] });
      }
      return {
        link,
        person: {
          title: `${application.account.firstName} ${application.account.lastName}`,
          image: application.account.imgs[0]?.small || PLACEHOLDER_IMAGE_USER,
          type: 'breeder',
        },
        onActionsClick: onActionsClick?.(application),
        date: dayjs(application.animal?.birthday ?? application.createdAt),
        status: {
          content: translate(`enum.dogBreederApplicationStatus.${application.status}`),
          theme: COLOR_PER_DOG_BREEDER_APPLICATION_STATUS[application.status],
        },
        breed: {
          title: application.breedPreference.translations[0].name ?? '',
        },
      };
    },
    [onActionsClick, routeLocationDescriptor, translate, type],
  );

  const formattedApplicationsCards = useMemo((): MtApplicationsPageData['cards'] => {
    return applications.map((application) => formatApplicationsCard(application));
  }, [applications, formatApplicationsCard]);

  return { formattedApplicationsCards, formatApplicationsCard };
};
